import { useState, useMemo, useEffect, useRef } from "react";
import { useReportTemplateContext } from "../ReportTemplate/ReportTemplateContext";
import { updateReportSection, createSectionForReport, updateReport, deleteReportSection, createImageSubcategory, getImageSubcategories, getDynamicPTSProjects, getDynamicPTS, saveDynamicPTS, createDynamicPTSProject, createMittausProjekti, createMittausKohde, getMittausProjektitByPTS, getTemplateByType, getReportSectionsByReportId } from "../../../api/api";
import { SUBSECTION_TEMPLATES } from "../../../assets/subsectionTemplates";
import { buildSubsectionBlocks, buildTemplateSentinelBlock, TEMPLATE_SENTINEL, isSentinelTitle, isSectionTemplateSentinel, getSectionTemplateCategory } from "../../../assets/subsectionBlockTemplates";
import ConfirmModal from "../../ConfirmModal";
import { deepCloneWithFreshIds } from "../../utils/createBlockID";
import {
  JULKISIVU_MEASUREMENT_BLOCKS,
  injectPTSIds,
  applyAutoSubcategory,
  resolveAllTags,
  buildChipSectionOrder,
  uniqueProjectName,
} from '../../../utils/subsectionUtils';

const AUTO_SUBCATEGORY_REPORT_TYPES = new Set(['julkisivujen_kuntotutkimus', 'ikkunoiden_ja_ulko-ovien_kuntoarvio']);
const AUTO_PTS_REPORT_TYPES = new Set(['julkisivujen_kuntotutkimus', 'ryomintatilojen_kuntotutkimus', 'vesikaton_kuntotutkimus', 'ikkuna_ovi_raportti', 'ikkunoiden_ja_ulko-ovien_kuntoarvio']);


function findSentinelForCategory(blocks, category) {
  const categoryTag = `{{subsection_template_${category.toLowerCase()}}}`;
  return (
    blocks.find(b => b.type === 'subsection' && b.data?.title?.includes(categoryTag)) ??
    blocks.find(b => b.type === 'subsection' && isSentinelTitle(b.data?.title)) ??
    null
  );
}

const CATEGORY_ICONS = {
  Julkisivut: "bi-building",
  Ikkunat: "bi-columns-gap",
  Ovet: "bi-door-open",
  Parvekkeet: "bi-aspect-ratio",
  Vesikatot: "bi-house-up",
};

function buildNewSectionOrder(sections, report, newSectionId, newCategory) {
  const sectionMap = Object.fromEntries(sections.map(s => [s.id, s]));
  const knownIds = new Set(sections.map(s => s.id));
  let orderedIds = Array.isArray(report?.section_order)
    ? report.section_order.filter(id => knownIds.has(id))
    : sections.map(s => s.id);
  const inOrder = new Set(orderedIds);
  sections.forEach(s => { if (!inOrder.has(s.id)) orderedIds.push(s.id); });

  const get = (id) => sectionMap[id];
  const categoryRank = Object.fromEntries(SUBSECTION_TEMPLATES.map((t, i) => [t.category, i]));
  const newRank = categoryRank[newCategory] ?? Infinity;

  const getTemplateCategory = (id) => {
    const s = get(id);
    return SUBSECTION_TEMPLATES.find(t =>
      s?.name?.toLowerCase() === t.category.toLowerCase()
    )?.category ?? null;
  };

  // Find where existing template sections live in the order
  const templateIndices = orderedIds.map((id, i) => getTemplateCategory(id) ? i : -1).filter(i => i >= 0);

  let insertAt;
  if (templateIndices.length === 0) {
    // No template sections yet — use yhteenveto/allekirjoitukset as anchors
    const yhteenvetoIdx = orderedIds.findIndex(id => {
      const s = get(id);
      return s?.type === "yhteenveto" || s?.name?.toLowerCase() === "yhteenveto";
    });
    const allekirjoituksetIdx = orderedIds.findIndex(id => {
      const s = get(id);
      return s?.type === "allekirjoitukset" || s?.name?.toLowerCase() === "allekirjoitukset";
    });
    const base = yhteenvetoIdx !== -1 ? yhteenvetoIdx + 1 : orderedIds.length;
    const cap = allekirjoituksetIdx !== -1 && allekirjoituksetIdx >= base
      ? allekirjoituksetIdx
      : orderedIds.length;
    insertAt = Math.min(base, cap);
  } else {
    // Sort among existing template sections by rank
    const min = Math.min(...templateIndices);
    const max = Math.max(...templateIndices);
    insertAt = min;
    for (let i = min; i <= max; i++) {
      const cat = getTemplateCategory(orderedIds[i]);
      if (cat && (categoryRank[cat] ?? Infinity) < newRank) insertAt = i + 1;
    }
  }

  return [
    ...orderedIds.slice(0, insertAt),
    newSectionId,
    ...orderedIds.slice(insertAt),
  ];
}

function parseContent(raw) {
  if (!raw) return { blocks: [] };
  if (typeof raw === "object") return raw;
  try { return JSON.parse(raw); } catch { return { blocks: [] }; }
}

const REPORT_TYPE_LABELS = {
  julkisivujen_kuntotutkimus: 'Julkisivujen kuntotutkimus',
  ikkuna_ovi_raportti: 'Ikkuna- ja oviraportit',
  'ikkunoiden_ja_ulko-ovien_kuntoarvio': 'Ikkuna- ja oviraportit',
  kuntoarvio: 'Kuntoarvio',
  kuntoraportti: 'Kuntoraportti',
  
  vesikaton_kuntotutkimus: 'Vesikaton kuntotutkimus',
  ryomintatilojen_kuntotutkimus: 'Ryömintätilojen kuntotutkimus',
  lvi_raportti: 'LVI-kuntoarvio',
};

function defaultProjectName(reportType) {
  const label = REPORT_TYPE_LABELS[reportType] ?? (reportType ?? 'Projekti').replace(/_/g, ' ');
  const date = new Date().toLocaleDateString('fi-FI');
  return `${label} ${date}`;
}



export default function SubsectionBuilderBlock({ data, onChange }) {
  const { sections, updateSection, report, fetchReportData } = useReportTemplateContext();
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [loadingCategory, setLoadingCategory] = useState(null);
  const [savingSubsection, setSavingSubsection] = useState(null);
  const [customInputs, setCustomInputs] = useState({});
  const [errors, setErrors] = useState({});
  const [fetchedSubcategories, setFetchedSubcategories] = useState({});
  const [confirmModal, setConfirmModal] = useState(null);
  const [ptsProjects, setPtsProjects] = useState([]);
  const [ptsLoaded, setPtsLoaded] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);
  const [showNewProjectForm, setShowNewProjectForm] = useState(false);
  const pendingResolveRef = useRef(null);

  const linkedProjectId = data?.linkedProjectId ?? null;

  // Pre-fill project name once report type is known (report loads async after mount)
  useEffect(() => {
    if (report?.type) setNewProjectName(prev => prev || defaultProjectName(report.type));
  }, [report?.type]);

  useEffect(() => {
    const id = report?.id_kiinteisto;
    if (!id) return;
    getDynamicPTSProjects(id)
      .then(async res => {
        const list = res.data ?? [];
        setPtsProjects(list);
        console.log("REPORT TYPE FOR AUTO PTS:", report?.type);
console.log(
  "AUTO PTS MATCH:",
  AUTO_PTS_REPORT_TYPES.has(report?.type)
);
        if (!data?.linkedProjectId && report?.type && AUTO_PTS_REPORT_TYPES.has(report.type)) {
          const name = uniqueProjectName(defaultProjectName(report.type), list);
          const res2 = await createDynamicPTSProject(id, name);
          const newProject = res2.data;
          setPtsProjects(prev => [...prev, newProject]);
          onChange?.({ ...data, linkedProjectId: newProject.id });
        }
      })
      .catch(() => {})
      .finally(() => setPtsLoaded(true));
  }, [report?.id_kiinteisto]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateProject = async () => {
    const name = newProjectName.trim() || 'Rakennetekniikka';
    const id = report?.id_kiinteisto;
    if (!id) return;
    setCreatingProject(true);
    try {
      const res = await createDynamicPTSProject(id, name);
      const newProject = res.data;
      setPtsProjects(prev => [...prev, newProject]);
      onChange?.({ ...data, linkedProjectId: newProject.id });
      setNewProjectName('');
      setShowNewProjectForm(false);
    } catch (err) {
      console.warn('[SubsectionBuilderBlock] createProject failed:', err);
    } finally {
      setCreatingProject(false);
    }
  };

  const syncPTSSection = async (categoryName, action) => {
    if (!linkedProjectId) return;
    try {
      const { data: ptsData } = await getDynamicPTS(linkedProjectId);
      const existing = ptsData.sections ?? [];
      let updated;
      if (action === 'add') {
        if (existing.find(s => s.name === categoryName)) return;
        updated = [...existing, { name: categoryName, kl_rating: 'KL3', sort_order: existing.length, entries: [] }];
      } else {
        updated = existing.filter(s => s.name !== categoryName);
      }
      await saveDynamicPTS(linkedProjectId, updated, ptsData.name);
    } catch (err) {
      console.warn('[SubsectionBuilderBlock] PTS sync failed:', err);
    }
  };

  useEffect(() => {
    SUBSECTION_TEMPLATES.forEach(template => {
      getImageSubcategories(template.imageCategory)
        .then(res => setFetchedSubcategories(prev => ({
          ...prev,
          [template.category]: res.data.map(r => r.subcategory),
        })))
        .catch(() => {});
    });
  }, []);

  // Keep data.selections in sync with what chip sections actually exist in DB.
  // The selections update in handleAddSubsection can be lost if the page is
  // refreshed within the debounce window, so we re-derive from sections.
  useEffect(() => {
    const chipTemplates = SUBSECTION_TEMPLATES.filter(t => t.useSectionTemplate);
    const derivedSelections = {};
    chipTemplates.forEach(tmpl => {
      const chipType = `section_chip_${tmpl.category.toLowerCase()}`;
      const names = sections.filter(s => s.type === chipType).map(s => s.name);
      if (names.length) derivedSelections[tmpl.category] = names;
    });
    // Only update if something is actually out of sync
    const current = data?.selections ?? {};
    const hasChanged = JSON.stringify(derivedSelections) !== JSON.stringify(current);
    if (hasChanged) {
      onChange?.({ ...data, selections: derivedSelections });
    }
  }, [sections]); // eslint-disable-line react-hooks/exhaustive-deps

  // Backfill: chip sections whose ImageBlocks are missing subCategory get it filled in.
  useEffect(() => {
    if (!AUTO_SUBCATEGORY_REPORT_TYPES.has(report?.type)) return;
    const chipTemplates = SUBSECTION_TEMPLATES.filter(t => t.useSectionTemplate && t.imageCategory);
    sections.forEach(async (section) => {
      const tmpl = chipTemplates.find(t => section.type === `section_chip_${t.category.toLowerCase()}`);
      if (!tmpl) return;
      let parsed;
      try { parsed = typeof section.content === 'string' ? JSON.parse(section.content) : (section.content ?? {}); } catch { return; }
      const updatedBlocks = applyAutoSubcategory(parsed.blocks ?? [], section.name, tmpl.imageCategory);
      if (JSON.stringify(updatedBlocks) === JSON.stringify(parsed.blocks ?? [])) return;
      const newContent = JSON.stringify({ ...parsed, blocks: updatedBlocks });
      try {
        await updateReportSection(section.id, { content: newContent });
        updateSection({ ...section, content: newContent });
      } catch (err) {
        console.warn('[SubsectionBuilder] subcategory backfill failed:', err);
      }
    });
  }, [sections]); // eslint-disable-line react-hooks/exhaustive-deps

  // Backfill: chip sections created before mittausProjektiId injection was in place
  // (or where the selections onChange was lost) get their projects created here.
  useEffect(() => {
    if (!linkedProjectId) return;
    const chipTemplates = SUBSECTION_TEMPLATES.filter(t => t.useSectionTemplate);
    sections.forEach(async (section) => {
      const tmpl = chipTemplates.find(t => section.type === `section_chip_${t.category.toLowerCase()}`);
      if (!tmpl) return;
      let parsed;
      try { parsed = typeof section.content === 'string' ? JSON.parse(section.content) : (section.content ?? {}); } catch { return; }
      const allBlocks = (function flatten(blocks) {
        return blocks.flatMap(b => [b, ...(b.data?.blocks ? flatten(b.data.blocks) : [])]);
      })(parsed.blocks ?? []);
      const missingTypes = Object.keys(JULKISIVU_MEASUREMENT_BLOCKS).filter(type =>
        allBlocks.some(b => b.type === type && !b.data?.mittausProjektiId)
      );
      if (!missingTypes.length) return;
      try {
        const blockTypeToMittausId = {};
        await Promise.all(missingTypes.map(async (blockType) => {
          const tyyppi = JULKISIVU_MEASUREMENT_BLOCKS[blockType];
          // Use the chip's own name so each chip keeps its own measurement data
          let projId = null;
          try {
            const existing = (await getMittausProjektitByPTS(linkedProjectId, tyyppi)).data ?? [];
            const match = existing.find(p => p.name === section.name);
            if (match) { projId = match.id; }
          } catch {}
          if (!projId) {
            const r = await createMittausProjekti(linkedProjectId, tyyppi, section.name);
            projId = r.data.id;
            if (blockType === 'PoranayteBlock') {
              await createMittausKohde(projId, 'Rakenneosa 1', 0);
            }
          }
          blockTypeToMittausId[blockType] = projId;
        }));
        const newContent = injectPTSIds(parsed, linkedProjectId, blockTypeToMittausId);
        await updateReportSection(section.id, { content: JSON.stringify(newContent) });
        await fetchReportData();
      } catch (err) {
        console.warn('[SubsectionBuilder] backfill mittausProjekti failed for', section.name, err);
      }
    });
  }, [linkedProjectId, sections.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const requestConfirm = (opts) => new Promise(resolve => {
    pendingResolveRef.current = resolve;
    setConfirmModal(opts);
  });

  const handleConfirm = () => {
    setConfirmModal(null);
    pendingResolveRef.current?.(true);
    pendingResolveRef.current = null;
  };

  const handleCancel = () => {
    setConfirmModal(null);
    pendingResolveRef.current?.(false);
    pendingResolveRef.current = null;
  };

  const addedTitles = useMemo(() => {
    const titles = new Set();
    SUBSECTION_TEMPLATES.forEach(template => {
      if (template.useSectionTemplate) {
        (data?.selections?.[template.category] ?? []).forEach(t => titles.add(t));
        // Also check sections directly — catches sections that exist in DB but are missing
        // from data.selections (e.g. after page reload before auto-save completed)
        const chipType = `section_chip_${template.category.toLowerCase()}`;
        sections.filter(s => s.type === chipType).forEach(s => titles.add(s.name));
        return;
      }
      const section = sections.find(s =>
        s.name?.toLowerCase() === template.category.toLowerCase() ||
        s.type === template.category.toLowerCase()
      );
      if (!section) return;
      const parsed = parseContent(section.content);
      (parsed.blocks ?? []).forEach(block => {
        if (block.type === "subsection" && block.data?.title && !isSentinelTitle(block.data.title)) {
          titles.add(block.data.title);
        }
      });
    });
    return titles;
  }, [sections, data?.selections]);

  const findSection = (template) =>
    sections.find(s =>
      s.name?.toLowerCase() === template.category.toLowerCase() ||
      s.type === template.category.toLowerCase()
    ) ?? null;

  const handleCategoryToggle = async (template) => {
    setErrors(prev => ({ ...prev, [template.category]: null }));

    if (template.useSectionTemplate) return;

    const existing = findSection(template);

    if (existing) {
      const confirmed = await requestConfirm({
        title: "Poista osio",
        message: `Poistetaanko osio "${existing.name}" ja kaikki sen sisältö?`,
        confirmText: "Poista",
        confirmVariant: "danger",
      });
      if (!confirmed) return;
      setLoadingCategory(template.category);
      try {
        await deleteReportSection(existing.id);
        await fetchReportData();
        await syncPTSSection(template.category, 'remove');
      } catch (err) {
        console.error("[SubsectionBuilderBlock] deleteSection failed:", err);
        setErrors(prev => ({ ...prev, [template.category]: err?.response?.data?.message || err.message || "Poisto epäonnistui" }));
      } finally {
        setLoadingCategory(null);
      }
    } else {
      if (!report?.id) {
        setErrors(prev => ({ ...prev, [template.category]: "Raportin ID puuttuu." }));
        return;
      }
      const confirmed = await requestConfirm({
        title: "Luo osio",
        message: `Luodaanko uusi osio "${template.category}"?`,
        confirmText: "Luo",
        confirmVariant: "success",
      });
      if (!confirmed) return;
      setLoadingCategory(template.category);
      try {
        const initialContent = template.autoPopulate
          ? JSON.stringify({ blocks: [buildSubsectionBlocks(template.category, template.category)] })
          : "";
        const res = await createSectionForReport({
          report_id: report.id,
          data: {
            name: template.category,
            type: template.category.toLowerCase(),
            content: initialContent,
            template: !report.id_kiinteisto,
          },
        });
        const newOrder = buildNewSectionOrder(sections, report, res.data.id, template.category);
        await updateReport(report.id, { section_order: newOrder });
        await fetchReportData();
        await syncPTSSection(template.category, 'add');
      } catch (err) {
        console.error("[SubsectionBuilderBlock] createSection failed:", err);
        setErrors(prev => ({ ...prev, [template.category]: err?.response?.data?.message || err.message || "Osion luonti epäonnistui" }));
      } finally {
        setLoadingCategory(null);
      }
    }
  };

  const handleAddSubsection = async (title, template, isCustom = false) => {
    const trimmed = title.trim();
    if (!trimmed || savingSubsection) return;

    const allSuggestions = [...new Set([...template.suggestions, ...(fetchedSubcategories[template.category] ?? [])])];

    if (isCustom) {
      const duplicate = allSuggestions.find(s => s.toLowerCase() === trimmed.toLowerCase());
      if (duplicate) {
        setErrors(prev => ({ ...prev, [template.category]: `Aliluokka "${duplicate}" on jo olemassa — valitse se listalta.` }));
        return;
      }
    }

    if (addedTitles.has(trimmed)) {
      setErrors(prev => ({ ...prev, [template.category]: `Aliosio "${trimmed}" on jo lisätty tähän raporttiin.` }));
      return;
    }

    // === useSectionTemplate: create a whole new report section from the sentinel template ===
    if (template.useSectionTemplate) {
      const confirmed = await requestConfirm({
        title: "Luo osio",
        message: `Luodaanko uusi osio "${trimmed}"?`,
        confirmText: "Luo",
        confirmVariant: "success",
      });
      if (!confirmed) return;

      setSavingSubsection(trimmed);
      setErrors(prev => ({ ...prev, [template.category]: null }));

      try {
            const templateKey =
        template.templateKey ?? template.category.toLowerCase();

      const isSentinel = (s) =>
        isSectionTemplateSentinel(s.name) &&
        getSectionTemplateCategory(s.name)?.toLowerCase() ===
          templateKey.toLowerCase();

        let templateSection = sections.find(isSentinel);

        if (!templateSection) {
          const typesToTry = [
            report.type,
            'julkisivujen_kuntotutkimus',
            'ikkunoiden_ja_ulko-ovien_kuntoarvio',
          ].filter((t, i, arr) => t && arr.indexOf(t) === i);

          for (const reportType of typesToTry) {
            if (templateSection) break;
            try {
              const tmplRes = await getTemplateByType(reportType);
              const tmplReport = tmplRes.data;
              if (tmplReport?.id) {
                const secRes = await getReportSectionsByReportId(tmplReport.id);
                templateSection = (secRes.data ?? []).find(isSentinel);
              }
            } catch (err) {
              console.warn(`[SubsectionBuilder] template lookup failed for type ${reportType}:`, err);
            }
          }
        }

let sectionContent;

const parsedTemplate = templateSection
  ? parseContent(templateSection.content)
  : null;

const hasTemplateContent =
  Array.isArray(parsedTemplate?.blocks) &&
  parsedTemplate.blocks.length > 0;

if (hasTemplateContent) {
  sectionContent = resolveAllTags(
    deepCloneWithFreshIds(parsedTemplate),
    trimmed
  );
} else {
  const templateBlock = buildTemplateSentinelBlock(
    template.category,
    template.imageCategory
  );

  const resolvedBlock = resolveAllTags(
    deepCloneWithFreshIds(templateBlock),
    trimmed
  );

  sectionContent = {
    blocks: resolvedBlock?.data?.blocks ?? [],
  };
}


        if (AUTO_SUBCATEGORY_REPORT_TYPES.has(report?.type)) {
          sectionContent = {
            ...sectionContent,
            blocks: (sectionContent.blocks ?? []).map(b => applyAutoSubcategory(b, trimmed, template.imageCategory)),
          };
        }

        // Auto-create a mittausProjekti per chip so each chip's measurement tables
        // are independent. The chip name (trimmed) is used as the project name.
        // DynamicPTS section names use ptsName separately via syncPTSSection.
        if (template.useSectionTemplate && linkedProjectId) {
          try {
            const blockTypeToMittausId = {};
            await Promise.all(
              Object.entries(JULKISIVU_MEASUREMENT_BLOCKS).map(async ([blockType, tyyppi]) => {
                let projId = null;
                try {
                  const existing = (await getMittausProjektitByPTS(linkedProjectId, tyyppi)).data ?? [];
                  const match = existing.find(p => p.name === trimmed);
                  if (match) projId = match.id;
                } catch {}
                if (!projId) {
                  const r = await createMittausProjekti(linkedProjectId, tyyppi, trimmed);
                  projId = r.data.id;
                  if (blockType === 'PoranayteBlock') {
                    await createMittausKohde(projId, 'Rakenneosa 1', 0);
                  }
                }
                blockTypeToMittausId[blockType] = projId;
              })
            );
            sectionContent = injectPTSIds(sectionContent, linkedProjectId, blockTypeToMittausId);
          } catch (err) {
            console.warn('[SubsectionBuilder] auto-create mittausProjektit failed:', err);
          }
        }

        const chipType = `section_chip_${template.category.toLowerCase()}`;
        const res = await createSectionForReport({
          report_id: report.id,
          data: {
            name: trimmed,
            type: chipType,
            content: JSON.stringify(sectionContent),
            template: !report.id_kiinteisto,
          },
        });
        const newOrder = buildChipSectionOrder(sections, report, res.data.id, template.category);
        await updateReport(report.id, { section_order: newOrder });
        // Save selections before fetchReportData so the context refresh doesn't
        // arrive before the debounced save and silently drop the update.
        const prevSelections = data?.selections?.[template.category] ?? [];
        onChange?.({ ...data, selections: { ...(data?.selections ?? {}), [template.category]: [...prevSelections, trimmed] } });

        await fetchReportData();
        await syncPTSSection(template.ptsName ?? trimmed, 'add');
        setCustomInputs(prev => ({ ...prev, [template.category]: "" }));
        if (template.imageCategory) {
          createImageSubcategory({ image_category: template.imageCategory, subcategory: trimmed })
            .catch(err => console.warn("[SubsectionBuilder] createImageSubcategory failed:", err));
        }
      } catch (err) {
        console.error("[SubsectionBuilder] createChipSection failed:", err);
        setErrors(prev => ({ ...prev, [template.category]: err?.response?.data?.message || err.message || "Osion luonti epäonnistui" }));
      } finally {
        setSavingSubsection(null);
      }
      return;
    }

    // === Original path: add a subsection block inside the container section ===
    const section = findSection(template);
    if (!section) return;

    const confirmed = await requestConfirm(
      isCustom
        ? {
            title: "Luo uusi aliluokka",
            message: `Oletko varma että haluat luoda aliluokan "${trimmed}"? Tarkasta oikeinkirjoitus ja uudelleenkäytettävyys, jotta aliluokkaa ei luoda turhaan.`,
            confirmText: "Luo aliluokka",
            confirmVariant: "primary",
          }
        : {
            title: "Lisää aliosio",
            message: `Lisätäänkö aliosio "${trimmed}" osioon "${section.name}"?`,
            confirmText: "Lisää",
            confirmVariant: "success",
          }
    );
    if (!confirmed) return;

    setSavingSubsection(trimmed);
    setErrors(prev => ({ ...prev, [template.category]: null }));

    try {
      const parsed = parseContent(section.content);

      // Prefer template block from the template report (always up-to-date).
      // Fall back to locally stored sentinel, then to buildSubsectionBlocks.
      let templateBlock = null;
      if (report.id_kiinteisto && report.type) {
        try {
          const tmplRes = await getTemplateByType(report.type);
          const tmplReport = tmplRes.data;
          console.log('[SubsectionBuilder] template report for type', report.type, '→', tmplReport);
          if (tmplReport?.id) {
            const secRes = await getReportSectionsByReportId(tmplReport.id);
            const allSecs = secRes.data ?? [];
            console.log('[SubsectionBuilder] template report sections:', allSecs.map(s => ({ id: s.id, name: s.name, type: s.type })));
            const matchSec = allSecs.find(s =>
              s.name?.toLowerCase() === template.category.toLowerCase() ||
              s.type === template.category.toLowerCase()
            );
            console.log('[SubsectionBuilder] matched section for category', template.category, '→', matchSec?.id, matchSec?.name);
            if (matchSec) {
              const tmplParsed = parseContent(matchSec.content);
              const blocks = tmplParsed.blocks ?? [];
              console.log('[SubsectionBuilder] template section blocks:', blocks.map(b => ({ type: b.type, title: b.data?.title })));
              templateBlock = findSentinelForCategory(blocks, template.category);
              console.log('[SubsectionBuilder] sentinel block found:', !!templateBlock);
            }
          }
        } catch (err) {
          console.warn('[SubsectionBuilder] template lookup failed:', err);
        }
      }
      if (!templateBlock) {
        templateBlock = findSentinelForCategory(parsed.blocks ?? [], template.category);
      }

      let newBlock;
      if (templateBlock) {
        let resolved = resolveAllTags(deepCloneWithFreshIds(templateBlock), trimmed);
        resolved = { ...resolved, data: { ...resolved.data, title: resolved.data.title.trim() } };
        newBlock = resolved;
      } else {
        newBlock = buildSubsectionBlocks(trimmed, template.category);
      }
      if (AUTO_SUBCATEGORY_REPORT_TYPES.has(report?.type)) {
        newBlock = applyAutoSubcategory(newBlock, trimmed, template.imageCategory);
      }
      const newContent = { ...parsed, blocks: [...(parsed.blocks ?? []), newBlock] };
      const newString = JSON.stringify(newContent);

      await updateReportSection(section.id, { content: newString });
      updateSection({ ...section, content: newString });
      setCustomInputs(prev => ({ ...prev, [template.category]: "" }));
      // Optimistically add chip so it appears immediately
      setFetchedSubcategories(prev => {
        const existing = prev[template.category] ?? [];
        if (existing.includes(trimmed)) return prev;
        return { ...prev, [template.category]: [...existing, trimmed].sort() };
      });
      createImageSubcategory({ image_category: template.imageCategory, subcategory: trimmed })
        .catch(err => console.warn("[SubsectionBuilderBlock] createImageSubcategory failed:", err));
    } catch (err) {
      console.error("[SubsectionBuilderBlock] addSubsection failed:", err);
      setErrors(prev => ({
        ...prev,
        [template.category]: err?.response?.data?.message || err.message || "Tallennus epäonnistui",
      }));
    } finally {
      setSavingSubsection(null);
    }
  };

  const handleRemoveSubsection = async (title, template) => {
    const confirmed = await requestConfirm({
      title: "Poista aliosio",
      message: `Poistetaanko aliosio "${title}"?`,
      confirmText: "Poista",
      confirmVariant: "danger",
    });
    if (!confirmed) return;

    if (template.useSectionTemplate) {
      const chipType = `section_chip_${template.category.toLowerCase()}`;
      const chipSection = sections.find(s => s.type === chipType && s.name === title);
      if (!chipSection) return;
      setSavingSubsection(title);
      setErrors(prev => ({ ...prev, [template.category]: null }));
      const remainingChips = (data?.selections?.[template.category] ?? []).filter(t => t !== title);
      try {
        await deleteReportSection(chipSection.id);
        await fetchReportData();
        if (remainingChips.length === 0) await syncPTSSection(template.ptsName ?? title, 'remove');
        const prevSelections = data?.selections?.[template.category] ?? [];
        onChange?.({ ...data, selections: { ...(data?.selections ?? {}), [template.category]: prevSelections.filter(t => t !== title) } });
      } catch (err) {
        console.error("[SubsectionBuilder] removeChipSection failed:", err);
        setErrors(prev => ({ ...prev, [template.category]: err?.response?.data?.message || err.message || "Poisto epäonnistui" }));
      } finally {
        setSavingSubsection(null);
      }
      return;
    }

    const section = findSection(template);
    if (!section) return;

    setSavingSubsection(title);
    setErrors(prev => ({ ...prev, [template.category]: null }));

    try {
      const parsed = parseContent(section.content);
      const newBlocks = (parsed.blocks ?? []).filter(
        b => !(b.type === "subsection" && b.data?.title === title)
      );
      const newString = JSON.stringify({ ...parsed, blocks: newBlocks });
      await updateReportSection(section.id, { content: newString });
      updateSection({ ...section, content: newString });
    } catch (err) {
      console.error("[SubsectionBuilderBlock] removeSubsection failed:", err);
      setErrors(prev => ({ ...prev, [template.category]: err?.response?.data?.message || err.message || "Poisto epäonnistui" }));
    } finally {
      setSavingSubsection(null);
    }
  };

  const handleSubsectionToggle = (title, template) =>
    addedTitles.has(title)
      ? handleRemoveSubsection(title, template)
      : handleAddSubsection(title, template, false);

  return (
    <div style={{ fontSize: "0.875rem" }}>
      {ptsLoaded && ptsProjects.length === 0 ? (
        <div className="mb-3 p-2 rounded border border-warning bg-warning bg-opacity-10">
          <div className="text-muted small mb-2">
            <i className="bi bi-exclamation-triangle me-1 text-warning" />
            Ei kuntoarvio PTS-projekteja. Luo projekti linkitystä varten:
          </div>
          <div className="d-flex gap-2 align-items-center">
            <input
              type="text"
              className="form-control form-control-sm"
              style={{ minWidth: 260, flex: '1 1 auto', maxWidth: 400 }}
              placeholder="Projektin nimi…"
              value={newProjectName}
              onChange={e => setNewProjectName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !creatingProject && handleCreateProject()}
              autoFocus
            />
            <button
              className="btn btn-sm btn-success"
              disabled={creatingProject || !report?.id_kiinteisto}
              onClick={handleCreateProject}
            >
              {creatingProject ? <span className="spinner-border spinner-border-sm" style={{ width: '0.8rem', height: '0.8rem' }} /> : 'Luo projekti'}
            </button>
          </div>
        </div>
      ) : (
        <div className="mb-3">
          <div className="d-flex align-items-center gap-2">
            <label className="text-muted small mb-0" style={{ whiteSpace: 'nowrap' }}>Linkitetty PTS-projekti:</label>
            <select
              className="form-select form-select-sm"
              style={{ maxWidth: 400, minWidth: 180 }}
              value={linkedProjectId ?? ''}
              onChange={e => onChange?.({ ...data, linkedProjectId: e.target.value ? Number(e.target.value) : null })}
            >
              <option value="">— Ei linkitetty —</option>
              {ptsProjects.map(p => (
                <option key={p.id} value={p.id}>{p.name || `Projekti ${p.id}`}</option>
              ))}
            </select>
            {linkedProjectId && (
              <button
                className="btn btn-sm btn-outline-secondary"
                style={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}
                onClick={() => {
                  setShowNewProjectForm(v => {
                    if (!v) setNewProjectName(defaultProjectName(report?.type));
                    return !v;
                  });
                }}
              >
                {showNewProjectForm ? 'Peru' : '+ Luo uusi'}
              </button>
            )}
          </div>
          {showNewProjectForm && linkedProjectId && (
            <div className="d-flex gap-2 align-items-center mt-2">
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ minWidth: 260, flex: '1 1 auto', maxWidth: 400 }}
                placeholder="Projektin nimi…"
                value={newProjectName}
                onChange={e => setNewProjectName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !creatingProject && handleCreateProject()}
                autoFocus
              />
              <button
                className="btn btn-sm btn-success"
                disabled={creatingProject || !newProjectName.trim() || !report?.id_kiinteisto}
                onClick={handleCreateProject}
              >
                {creatingProject
                  ? <span className="spinner-border spinner-border-sm" style={{ width: '0.8rem', height: '0.8rem' }} />
                  : 'Luo projekti'}
              </button>
            </div>
          )}
        </div>
      )}

      {(report?.type === 'julkisivujen_kuntotutkimus' || report?.type === 'ikkunoiden_ja_ulko-ovien_kuntoarvio'
        ? SUBSECTION_TEMPLATES.filter(t => t.useSectionTemplate)
        : SUBSECTION_TEMPLATES
      ).map(template => {
        const { category, suggestions } = template;
        const fetched = fetchedSubcategories[category] ?? [];
        const allSuggestions = [...new Set([...suggestions, ...fetched])];
        const section = findSection(template);
        const isLoading = loadingCategory === category;
        const isSavingThis = !!savingSubsection;
        const customVal = customInputs[category] ?? "";
        const err = errors[category];

        const selectedNames = template.useSectionTemplate ? (data?.selections?.[category] ?? []) : null;
        const isActive = template.useSectionTemplate
          ? (selectedNames?.length ?? 0) > 0
          : !!section;
        const isExpanded = template.useSectionTemplate ? true : !!section;
        const categoryAddedCount = template.useSectionTemplate
          ? (selectedNames?.length ?? 0)
          : allSuggestions.filter(s => addedTitles.has(s)).length;

        return (
          <div key={category} className="mb-2">
            <div
              className="d-flex align-items-center gap-2 px-2 py-2 rounded"
              style={{
                cursor: isLoading ? "wait" : "pointer",
                backgroundColor: isActive ? "#f0fdf4" : "#f8f9fa",
                border: `1px solid ${isActive ? "#86efac" : "#dee2e6"}`,
                userSelect: "none",
              }}
              onClick={() => !isLoading && handleCategoryToggle(template)}
            >
              {isLoading && (
                <span
                  className="spinner-border spinner-border-sm text-success"
                  style={{ width: "0.85rem", height: "0.85rem", flexShrink: 0 }}
                />
              )}
              <i
                className={`bi ${CATEGORY_ICONS[category] ?? "bi-folder"}`}
                style={{ fontSize: "0.85rem", color: isActive ? "#16a34a" : "#6c757d" }}
              />
              <span style={{ fontWeight: 600, color: isActive ? "#15803d" : "#343a40" }}>
                {category}
              </span>
              {categoryAddedCount > 0 && (
                <span className="badge rounded-pill bg-success ms-auto" style={{ fontSize: "0.6rem" }}>
                  {categoryAddedCount} osiota
                </span>
              )}
            </div>

            {isExpanded && (
              <div
                className="pt-2 pb-1 px-2"
                style={{ borderLeft: "2px solid #86efac", marginLeft: "0.5rem" }}
              >
                {template.useSectionTemplate ? (
                  <>
                    <div className="d-flex flex-wrap gap-1 mb-2">
                      {allSuggestions.map(s => {
                        const isAdded = addedTitles.has(s);
                        return (
                          <button
                            key={s}
                            type="button"
                            disabled={savingSubsection === s}
                            onClick={() => handleSubsectionToggle(s, template)}
                            className={`btn btn-sm ${isAdded ? "btn-success" : "btn-outline-secondary"}`}
                            style={{ fontSize: "0.75rem", padding: "0.15rem 0.6rem" }}
                          >
                            {savingSubsection === s
                              ? <span className="spinner-border spinner-border-sm" style={{ width: "0.7rem", height: "0.7rem" }} />
                              : <>{isAdded && <i className="bi bi-check me-1" style={{ fontSize: "0.7rem" }} />}{s}</>
                            }
                          </button>
                        );
                      })}
                    </div>
                    <div className="d-flex gap-2">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Uuden osion nimi…"
                        value={customVal}
                        onChange={e => {
                          setCustomInputs(prev => ({ ...prev, [category]: e.target.value }));
                          setErrors(prev => ({ ...prev, [category]: null }));
                        }}
                        onKeyDown={e =>
                          e.key === "Enter" && !isSavingThis && handleAddSubsection(customVal, template, true)
                        }
                      />
                      <button
                        className="btn btn-outline-success btn-sm text-nowrap"
                        onClick={() => handleAddSubsection(customVal, template, true)}
                        disabled={!customVal.trim() || isSavingThis}
                      >
                        {isSavingThis && savingSubsection === customVal.trim()
                          ? <span className="spinner-border spinner-border-sm" style={{ width: "0.7rem", height: "0.7rem" }} />
                          : 'Luo osio'
                        }
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="d-flex flex-wrap gap-1 mb-2">
                      {allSuggestions.map(s => {
                        const isAdded = addedTitles.has(s);
                        return (
                          <button
                            key={s}
                            type="button"
                            disabled={savingSubsection === s}
                            onClick={() => handleSubsectionToggle(s, template)}
                            className={`btn btn-sm ${isAdded ? "btn-success" : "btn-outline-secondary"}`}
                            style={{ fontSize: "0.75rem", padding: "0.15rem 0.6rem" }}
                          >
                            {savingSubsection === s
                              ? <span className="spinner-border spinner-border-sm" style={{ width: "0.7rem", height: "0.7rem" }} />
                              : <>{isAdded && <i className="bi bi-check me-1" style={{ fontSize: "0.7rem" }} />}{s}</>
                            }
                          </button>
                        );
                      })}
                    </div>
                    <div className="d-flex gap-2">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Luo uusi…"
                        value={customVal}
                        onChange={e => {
                          setCustomInputs(prev => ({ ...prev, [category]: e.target.value }));
                          setErrors(prev => ({ ...prev, [category]: null }));
                        }}
                        onKeyDown={e =>
                          e.key === "Enter" && !isSavingThis && handleAddSubsection(customVal, template, true)
                        }
                      />
                      <button
                        className="btn btn-outline-success btn-sm text-nowrap"
                        onClick={() => handleAddSubsection(customVal, template, true)}
                        disabled={!customVal.trim() || isSavingThis}
                      >
                        Lisää
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {err && (
              <div
                className="mt-1 px-2 py-1 rounded"
                style={{
                  fontSize: "0.75rem",
                  color: "#842029",
                  backgroundColor: "#f8d7da",
                  border: "1px solid #f5c2c7",
                }}
              >
                <i className="bi bi-exclamation-triangle me-1" />
                {err}
              </div>
            )}
          </div>
        );
      })}

      {confirmModal && (
        <ConfirmModal
          show
          onClose={handleCancel}
          onConfirm={handleConfirm}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmText={confirmModal.confirmText}
          cancelText="Peru"
          confirmVariant={confirmModal.confirmVariant}
        />
      )}
    </div>
  );
}
